/* eslint-disable max-lines */
import { find, pathOr, propEq, uniqBy } from 'ramda';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { getAlbumsItemById, getCurrentAlbum, getCurrentAlbumList } from 'reactApp/modules/albums/albums.selector';
import { AttachesSelectors } from 'reactApp/modules/attaches/attaches.selectors';
import { contactsSelectors } from 'reactApp/modules/contacts/contacts.selectors';
import { getEditorItemById, isEditorStorage } from 'reactApp/modules/editor/editor.selectors';
import { getEmbeddedItemById } from 'reactApp/modules/embedded/embedded.selectors';
import { getFilesWithFaceIdxs, getItemInSelectedFace, getSelectedFaceId, hasMoreToLoad } from 'reactApp/modules/faces/faces.selectors';
import { FavoritesSelectors } from 'reactApp/modules/favorites/favorites.selectors';
import { getCurrentFeedCategory, getFeedItemById } from 'reactApp/modules/feed/feed.selectors';
import { getPathParts, isMediaFolderAndGalleryEnabled as isMedia } from 'reactApp/modules/file/utils';
import { getCurrentFilter } from 'reactApp/modules/filesFilter/filesFilter.selectors';
import { getCurrentGalleryCategory, getGalleryItemById } from 'reactApp/modules/gallery/gallery.selectors';
import { getCurrentChildIds, getCurrentFolderHome, getFolderListById, getHomeItemById } from 'reactApp/modules/home/home.selectors';
import { HomeItem } from 'reactApp/modules/home/home.types';
import { getIncomingIds, getIncomingItemById, getIncomingStorage } from 'reactApp/modules/incoming/incoming.selectors';
import {
    getCurrentRouteDocument,
    getDocumentChilds,
    getDocumentItemById,
} from 'reactApp/modules/personalDocuments/personalDocuments.selectors';
import {
    getCurrentPublicFolder,
    getCurrentPublicFolderChilds,
    getPublicItemById,
    isOwnPublic,
    isPublicUploadEnabled,
} from 'reactApp/modules/public/public.selectors';
import { PublicItem } from 'reactApp/modules/public/public.types';
import { getCurrentRouteId, getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getCurrentSearchFolder, getSearchItemById } from 'reactApp/modules/search/search.selectors';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getSharedIds, getSharedItemById, getSharedStorage } from 'reactApp/modules/shared/shared.selectors';
import {
    getSharedAutoDeleteFiledIds,
    getSharedAutoDeleteItemById,
    getSharedAutoDeleteStorage,
} from 'reactApp/modules/sharedAutoDelete/sharedAutoDelete.selectors';
import { sortItems } from 'reactApp/modules/sort/sort.helpers';
import { getSort } from 'reactApp/modules/sort/sort.selectors';
import { getCurrentStockFolder, getCurrentStockIds, getStockItemById } from 'reactApp/modules/stock/stock.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { getStoryItemById } from 'reactApp/modules/stories/stories.selectors';
import { getBinItemById, getOpenedBin } from 'reactApp/modules/trashbin/trashbin.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import {
    getCurrentGroup,
    getCurrentGroupTotalCount,
    getItemById as getQuotaItemById,
    isYearCurrentGroupFilter,
} from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import { isMobileGalleryViewMode } from 'reactApp/sections/MobileGalleryPage/MobileGalleryPage.helpers';
import { RootState } from 'reactApp/store';
import { Count, Sort } from 'reactApp/types/Tree';
import { galleryCrumb, trashbinCrumb } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer.data';
import { getDividerPeriodName, getPeriodNameByDate } from 'reactApp/utils/dateDivider';
import { noopUndefined } from 'reactApp/utils/helpers';
import { createSelector } from 'reselect';

import { getArchiveItemById } from '../viewer/viewer.selectors';

const isFolder = (item: any): boolean => item?.isFolder || item?.type === 'folder';
export const isDomainFolder = (item: any): boolean => isFolder(item) && item.kind === 'domain-folder';
export const isMountedFolder = (item: any): boolean => isFolder(item) && item.kind === 'mounted';
export const isSharedFolder = (item: any): boolean => isFolder(item) && item.kind === 'shared';
export const isPublicFolder = (item: any): boolean => isFolder(item) && Boolean(item.weblink);
export const isCameraUploadsFolder = (item: any): boolean => isFolder(item) && item?.kind === 'camera-upload';

const getRootState = (state: RootState) => state;

const getSelectedFunc = (storage) => {
    type GetItemByID = (state: RootState, id: string) => CloudItem | undefined;
    let selectFunc: GetItemByID = noopUndefined;

    if (storage === EStorageType.home) {
        selectFunc = getHomeItemById;
    } else if (storage === EStorageType.start) {
        // Для загрузчика на стартовой
        selectFunc = getHomeItemById;
    } else if (storage === EStorageType.sharedLinks) {
        selectFunc = getSharedItemById;
    } else if (storage === EStorageType.sharedAutodelete) {
        selectFunc = getSharedAutoDeleteItemById;
    } else if (storage === EStorageType.favorites) {
        selectFunc = FavoritesSelectors.getFavoriteById;
    } else if (storage === EStorageType.sharedIncoming) {
        selectFunc = getIncomingItemById;
    } else if (storage === EStorageType.public) {
        selectFunc = (state: RootState, id: string) => {
            const itemInFace = getItemInSelectedFace(state, id);

            return itemInFace ? itemInFace : getPublicItemById(state, id);
        };
    } else if (storage === EStorageType.attaches) {
        selectFunc = AttachesSelectors.getAttachById;
    } else if (storage === EStorageType.viewerAttaches) {
        selectFunc = AttachesSelectors.getViewerAttachById;
    } else if (storage === EStorageType.stock) {
        selectFunc = getStockItemById;
    } else if (storage === EStorageType.search) {
        selectFunc = getSearchItemById;
    } else if (storage === EStorageType.trashbin) {
        selectFunc = getBinItemById;
    } else if (isEditorStorage(storage)) {
        selectFunc = getEditorItemById;
    } else if (storage === EStorageType.feed) {
        selectFunc = getFeedItemById;
    } else if (storage === EStorageType.gallery) {
        selectFunc = getGalleryItemById;
    } else if (storage === EStorageType.documents) {
        selectFunc = getDocumentItemById;
    } else if (storage === EStorageType.story) {
        selectFunc = getStoryItemById;
    } else if (storage === EStorageType.albums) {
        selectFunc = getAlbumsItemById as GetItemByID;
    } else if (storage === EStorageType.quotaCleaner) {
        selectFunc = getQuotaItemById as any;
    } else if (storage === EStorageType.archive) {
        selectFunc = getArchiveItemById as any;
    } else if (storage === EStorageType.embedded) {
        selectFunc = getEmbeddedItemById as any;
    }

    return selectFunc;
};

export const getItemById = createSelector(
    getCurrentStorage,
    (state: RootState) => state,
    (state, id: string) => id,
    (storage, state, id) => getSelectedFunc(storage)(state, id)
);

export const getStorageItemById = createSelector(
    (state: RootState) => state,
    (state, storage: EStorageType | null) => storage || getCurrentStorage(state),
    (state, storage: EStorageType | null, id: string) => id,
    (state, storage, id) => getSelectedFunc(storage)(state, id)
);

export const getStorageItemByIdFromStorageOrCurrentOrHome = createSelector(
    (state: RootState) => state,
    (state, storage: EStorageType | undefined) => storage,
    (state, storage: EStorageType | undefined, id: string) => id,
    (state, targetStorage: EStorageType | undefined, id) => {
        const targetStorageItem = targetStorage && getStorageItemById(state, targetStorage, id);
        const currentStorageItem = !targetStorageItem && getItemById(state, id);
        return targetStorageItem || currentStorageItem || getStorageItemById(state, EStorageType.home, id);
    }
);

export const getStorageCurrentFolder = createSelector(
    getRootState,
    (state, storage: EStorageType): EStorageType => storage,
    (state, storage: EStorageType) => {
        if (storage === EStorageType.home) {
            return getCurrentFolderHome(state);
        } else if (storage === EStorageType.sharedLinks) {
            return getSharedStorage(state);
        } else if (storage === EStorageType.sharedAutodelete) {
            return getSharedAutoDeleteStorage(state);
        } else if (storage === EStorageType.favorites) {
            return FavoritesSelectors.getFavoritesStore(state);
        } else if (storage === EStorageType.sharedIncoming) {
            return getIncomingStorage(state);
        } else if (storage === EStorageType.public) {
            return getCurrentPublicFolder(state);
        } else if (storage === EStorageType.attaches) {
            return AttachesSelectors.getAttachesStore(state);
        } else if (storage === EStorageType.stock) {
            return getCurrentStockFolder(state);
        } else if (storage === EStorageType.search) {
            return getCurrentSearchFolder(state);
        } else if (storage === EStorageType.trashbin) {
            return getOpenedBin(state);
        } else if (storage === EStorageType.feed) {
            return getCurrentFeedCategory(state);
        } else if (storage === EStorageType.gallery) {
            return getCurrentGalleryCategory(state);
        } else if (storage === EStorageType.albums) {
            return getCurrentAlbum(state);
        } else if (storage === EStorageType.documents) {
            return getCurrentRouteDocument(state);
        } else if (storage === EStorageType.quotaCleaner) {
            return getCurrentGroup(state);
        }
    }
);

export type StorageCurrentFolder = ReturnType<typeof getStorageCurrentFolder>;

export const getCurrentFolder = createSelector(
    getRootState,
    (state) => getCurrentStorage(state) as EStorageType,
    (state, storage: EStorageType) => getStorageCurrentFolder(state, storage)
);

export const getCurrentItem = createSelector(
    getRootState,
    (state) => getCurrentRouteId(state),
    (state, id: string) => getItemById(state, id)
);

export const getCurrentCount = createSelector(
    getCurrentFolder,
    (folder): Count =>
        (folder && 'count' in folder && folder.count) || {
            folders: 0,
            files: 0,
            all: 0,
            loaded: 0,
        }
);

export const isCurrentLoaded = createSelector(getCurrentFolder, (folder): boolean =>
    Boolean(!!folder && 'isLoaded' in folder && folder.isLoaded)
);

export const getIds = createSelector(
    getRootState,
    (state, storage: EStorageType | undefined) => storage || getCurrentStorage(state),
    (state, storage: EStorageType | undefined): string[] => {
        let current: string[] = [];
        if (storage === EStorageType.home) {
            current = getCurrentChildIds(state);
        } else if (storage === EStorageType.sharedLinks) {
            current = getSharedIds(state);
        } else if (storage === EStorageType.sharedAutodelete) {
            current = getSharedAutoDeleteFiledIds(state);
        } else if (storage === EStorageType.favorites) {
            current = FavoritesSelectors.getFavoritesList(state);
        } else if (storage === EStorageType.sharedIncoming) {
            current = getIncomingIds(state);
        } else if (storage === EStorageType.public) {
            const selectedFaceId = getSelectedFaceId(state);
            if (selectedFaceId) {
                return getFilesWithFaceIdxs(state, selectedFaceId);
            }
            current = getCurrentPublicFolderChilds(state);
        } else if (storage === EStorageType.attaches) {
            return AttachesSelectors.getAttachesList(state);
        } else if (storage === EStorageType.viewerAttaches) {
            return AttachesSelectors.getViewerAttachesIds(state);
        } else if (storage === EStorageType.stock) {
            return getCurrentStockIds(state);
        } else if (storage === EStorageType.albums) {
            return getCurrentAlbumList(state);
        } else if (storage === EStorageType.search) {
            return getStorageCurrentFolder(state, storage)?.childs || [];
        } else if (storage === EStorageType.trashbin) {
            return getStorageCurrentFolder(state, storage)?.childs || [];
        } else if (storage === EStorageType.feed || storage === EStorageType.gallery) {
            return getStorageCurrentFolder(state, storage)?.childs || [];
        } else if (storage === EStorageType.documents) {
            return getDocumentChilds(state) || [];
        } else if (storage === EStorageType.quotaCleaner) {
            return getCurrentGroup(state)?.childs || [];
        }

        return current;
    }
);

export const sortIds = createSelector(
    (state: RootState, sort: Sort, storage: EStorageType) => getIds(state, storage),
    (state, sort: Sort) => sort,
    (state: RootState, sort: Sort, storage: EStorageType) => storage,
    (state: RootState, sort: Sort, storage: EStorageType, filter?: ((item) => boolean) | null) => filter || (() => true),
    (state: RootState) => state,
    (ids, sort, storage: EStorageType, filter: (item) => boolean, state) => {
        if (!sort?.type) {
            return ids;
        }
        const items = uniqBy(
            (item) => item.id,
            ids.map((id) => getStorageItemById(state, storage, id)).filter((item) => !!item && filter(item)) as CloudItem[]
        );

        let sortedItems: CloudItem[] = items;
        // client sort is off, sort on backend
        if (storage !== EStorageType.quotaCleaner) {
            sortedItems = sortItems(items, sort) as CloudItem[];
        }

        return sortedItems.map((item) => (item as CloudItem).id);
    }
);

export const filterIdsByAuthors = createSelector(
    getCurrentFilter,
    (state, ids: string[] | undefined, storage: EStorageType | undefined) => storage || getCurrentStorage(state),
    (state, ids: string[] | undefined) => ids || getIds(state, undefined),
    UserSelectors.getEmail,
    isOwnPublic,
    (state: RootState) => state,
    (filter, storage, ids, userEmail, isPublicOwner, state) => {
        const { isHome, isPublic } = getStorage(storage);

        if (filter !== 'all' && (isHome || isPublic)) {
            return ids.filter((id) => {
                const item = getStorageItemById(state, storage, id) as HomeItem | PublicItem | undefined;

                if (!item || (item.storage !== EStorageType.home && item.storage !== EStorageType.public)) {
                    return false;
                }

                if (item?.isFolder) {
                    return true;
                }

                if (item.author && filter.includes(item.author)) {
                    return true;
                }

                if (isHome && userEmail && !item?.author && filter.includes(userEmail)) {
                    return true;
                }

                if (isPublic && isPublicOwner && userEmail && !item?.author && filter.includes(userEmail)) {
                    return true;
                }

                return false;
            });
        }

        return ids;
    }
);

export const groupedIds = createSelector(
    (state) => state,
    (state: RootState, storage: EStorageType) => storage,
    (state, storage: EStorageType) => getStorageCurrentFolder(state, storage),
    getCurrentStorage,
    (state, storage: EStorageType, filterFolder?: boolean) => filterFolder,
    (state, storage: EStorageType) =>
        SettingsSelectors.getViewByStorage(state, storage, isMedia(state, getStorageCurrentFolder(state, storage))),
    (state, storage, folder, currentPage, filterFolder, viewMode) => {
        const selectedFaceId = getSelectedFaceId(state);
        const { isPublic, isFeed, isGallery } = getStorage(storage);

        if (isPublic && selectedFaceId) {
            return getFilesWithFaceIdxs(state, selectedFaceId);
        }

        const sort = getSort(state, folder, storage);
        const ids = sortIds(state, sort, storage, filterFolder ? (item) => !item.isFolder : null);

        if (isFeed && currentPage !== EStorageType.start && currentPage !== EStorageType.home && !filterFolder) {
            return getStorageItemsGroupedByPeriod(state, ids);
        }

        if (isGallery && isMobileGalleryViewMode(viewMode) && !filterFolder) {
            return getStorageItemsGroupedByPeriod(state, ids);
        }

        return filterIdsByAuthors(state, ids, storage);
    }
);

export const hasLoadMore = createSelector(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (state: RootState, storage: EStorageType | undefined, folder: CloudItem | undefined) => storage,
    (state: RootState, storage: EStorageType | undefined, folder: CloudItem | undefined) =>
        folder ?? getStorageCurrentFolder(state, storage || getCurrentStorage(state)),
    (state: RootState) => state,
    (storage, folder, state): boolean => {
        if (storage === EStorageType.public) {
            const selectedFaceId = getSelectedFaceId(state);
            if (selectedFaceId) {
                // @ts-ignore
                return hasMoreToLoad(state, selectedFaceId);
            }
        }

        return (folder && 'hasMoreToLoad' in folder && folder.hasMoreToLoad) || false;
    }
);

export const getItemsCount = createSelector(getIds, (ids) => ids.length);
export const getCurrentTotalCount = createSelector(filterIdsByAuthors, (ids) => ids.length);

export const getItemsTotalCount = createSelector(
    (state) => state,
    getCurrentStorage,
    (state, storage) => {
        if (storage === EStorageType.quotaCleaner) {
            return getCurrentGroupTotalCount(state);
        }

        return null;
    }
);

export const getStorageSelectedItems = createSelector(
    SelectionsSelectors.getSelectedIdxs,
    (state, storage) => storage || getCurrentStorage(state),
    (state: RootState) => state,
    (selected, storage, state) => {
        const selectFunc = getSelectedFunc(storage);

        return selected.map((id) => selectFunc(state, id)).filter((_) => _);
    }
);

export const getSelectedItems = createSelector(
    (state) => state,
    getCurrentStorage,
    (state, storage) => getStorageSelectedItems(state, storage)
);

export const mapFolderAuthorsWithContacts = createSelector(
    contactsSelectors.getContacts,
    (state, authors: string[]): string[] => authors,
    (contacts = [], authors = []): { email: string; name: string; nick: string }[] =>
        authors.map((author) => ({
            email: author,
            name: pathOr(author, ['name'], find(propEq('email', author), contacts || [])),
            nick: pathOr('', ['nick'], find(propEq('email', author), contacts || [])),
        }))
);

export const getFolderAuthors = createSelector(getCurrentFolder, (folder) => {
    return folder && 'authors' in folder ? Object.keys(folder.authors || {}) : [];
});

export const isFolderWithAuthor = createSelector(getFolderAuthors, UserSelectors.getEmail, (authors, userEmail) => {
    return authors.length > 1 || (authors.length === 1 && authors[0] !== userEmail);
});

export const getItemFolder = createSelector(
    (state, item: CloudItem | undefined) => item,
    getCurrentStorage,
    (state) => state,
    (item, storage, state) => {
        if (!item) {
            return undefined;
        } else if (item.isFolder) {
            return item;
        }
        const selectFunc = getSelectedFunc(storage);
        return selectFunc?.(state, item.parent);
    }
);

export const getBreadCrumbs = createSelector(
    (_: RootState, from?: string) => from,
    (state) => getCurrentStorage(state),
    (state: RootState) => state,
    (from, storage, state) => {
        const selectFunc = getSelectedFunc(storage);
        // shame on me
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        let item: CloudItem | undefined = from ? selectFunc(state, from) : getCurrentFolder(state);
        const list: CloudItem[] = [];

        if (!item) {
            return list;
        }

        do {
            list.push(item);
            item = selectFunc(state, item.parent);
        } while (item);

        return list.reverse();
    }
);

export const isRootCurrentFolder = createSelector(
    (state: RootState) => state,
    (state, storage) => getStorageCurrentFolder(state, storage),
    (state, folder) => !(folder && 'parent' in folder && folder.parent)
);

export const isMountedOrSharedFolder = createSelector(
    (state: RootState) => state,
    (state, item) => item,
    // @ts-ignore
    (state, item) => getBreadCrumbs(state, getItemFolder(state, item)?.id),
    getCurrentStorage,
    (state, item, breadCrumbs, storage) => {
        let folderId = '';
        let isMounted = false;
        let isShared = false;
        let isDomain = false;
        let is = breadCrumbs.some((cloudItem) => {
            folderId = cloudItem.id;
            isDomain = isDomainFolder(cloudItem);
            isMounted = isMountedFolder(cloudItem) || isDomain;
            isShared = isSharedFolder(cloudItem);

            return isMounted || isShared;
        });

        if (item && storage === EStorageType.sharedLinks && !isMounted) {
            const firstFolder = getPathParts(item.id)
                .filter((part) => Boolean(part))
                .shift();

            if (item.name !== firstFolder) {
                const firstFolderId = `/${firstFolder}`.toLowerCase();
                const rootItems = getFolderListById(state, ROOT_FOLDER_ID);
                rootItems.some((item) => {
                    if (item?.isMounted) {
                        isMounted = true;
                        is = is || item.isMounted;
                        return;
                    }

                    if (item?.id.toLowerCase() === firstFolderId) {
                        isMounted = isMountedFolder(item) || isDomainFolder(item);
                        is = is || isMounted;
                    }

                    return isMounted;
                });
            }
        }

        return {
            is,
            isDomain,
            isMounted,
            isShared,
            folderId,
        };
    }
);

export const isThisOrParentPublicFolder = createSelector(
    (state: RootState) => state,
    (state, item) => item,
    // @ts-ignore
    (state, item) => getBreadCrumbs(state, getItemFolder(state, item)?.id),
    (state, item, breadCrumbs) => breadCrumbs.some(isPublicFolder)
);

export const isReadOnlyMountedOrShared = createSelector(
    (state: RootState) => state,
    isMountedOrSharedFolder,
    (state, { is, folderId }) => {
        if (is && folderId) {
            const item = getItemById(state, folderId);
            return Boolean(item && 'isReadOnly' in item && item.isReadOnly);
        }

        return false;
    }
);

export const getMobileBreadcrumbs = createSelector(
    SettingsSelectors.getQueryParams,
    (state, storage) => storage,
    (state, storage) => getStorageCurrentFolder(state, storage),
    ({ query }, storage, currentFolder) => {
        switch (storage) {
            case EStorageType.trashbin:
                return trashbinCrumb[0];
            case EStorageType.gallery:
                return galleryCrumb[0];
            case EStorageType.search:
                if (query) {
                    return currentFolder;
                }

                return {
                    ...currentFolder,
                    name: 'Поиск',
                };
            default:
                return currentFolder;
        }
    }
);

export const getSelectedItem = (state) => getRootState(state).storage.selectedItem;

export const isUploadEnabled = createSelector(
    (state: RootState) => state,
    (state) => getCurrentStorage(state),
    (state) => getCurrentFolder(state),
    (state, storage, folder) => {
        const isAnonym = UserSelectors.isAnonymous(state);

        if (isAnonym) {
            return false;
        }

        if (storage === EStorageType.public) {
            return isPublicUploadEnabled(state);
        }

        if (storage === EStorageType.home) {
            return !isReadOnlyMountedOrShared(state, folder);
        }
    }
);

export const isEmptyFolder = createSelector(
    (state: RootState) => state,
    (state) => getCurrentFolder(state),
    (state, folder) => !folder?.childs.length && !folder?.isLoading && folder?.isLoaded
);

export const isCurrentFolderLoading = createSelector(
    (state: RootState) => state,
    (state) => getCurrentFolder(state),
    (state, folder) => folder?.isLoading && !folder?.isLoaded && !folder?.childs.length
);

export const getStorageItemsGroupedByPeriod = createSelector(
    (state: RootState) => state,
    getCurrentStorage,
    (state: RootState, ids: string[]) => ids,
    (state, storage, ids) => {
        if (!ids.length) {
            return [];
        }

        const now = Date.now();
        const firstItem = getStorageItemById(state, storage, ids[0]);
        const dateFirstItem = (firstItem && 'mtime' in firstItem && firstItem?.mtime) || now;
        let period = getPeriodNameByDate(dateFirstItem);

        const result = [
            {
                divider: true,
                text: getDividerPeriodName(period),
            },
        ];

        return ids.reduce((acc: any[], curr: string) => {
            const item = getStorageItemById(state, storage, curr);
            const date = (item && 'mtime' in item && item?.mtime) || now;
            const currPeriod = getPeriodNameByDate(date);

            if (period !== currPeriod) {
                period = currPeriod;
                acc.push({
                    divider: true,
                    text: getDividerPeriodName(currPeriod),
                });
            }
            acc.push(curr);
            return acc;
        }, result);
    }
);

export const isFolderOwner = createSelector(getCurrentStorage, isOwnPublic, (storage, isOwnPublic) => {
    const { isHome, isPublic } = getStorage(storage);

    return isHome || (isPublic && isOwnPublic);
});

export const getIsYearFilter = createSelector(
    (state: RootState) => state,
    getCurrentStorage,
    (state, storage) => {
        const { isQuotaCleaner } = getStorage(storage);
        if (isQuotaCleaner) {
            return isYearCurrentGroupFilter(state);
        }

        return false;
    }
);
